import { FC } from "react";
import { NumericInput } from "react-migration/components/NumericInput";
import debounce from "lodash/debounce";

import { MeasurementSystem } from "src/js/stores/user/store";
import { AllGeoJSON } from "src/js/util/turf";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import {
  convertUserUnitsToMeters,
  metersToWalkingMinutes,
  walkingMinutesToMeters,
} from "react-migration/lib/util/conversion";
import { radiusInUserUnits, unitOfMeasurement } from "react-migration/lib/util/measurements";
import { DrawingItem } from "src/js/types/DrawingItem";
import { DrawingsKeyboardEvent } from "react-migration/domains/comparables/typings/Drawings";

// Duplicated from drawings-frontend MAX_LENGTH_METERS. Importing it directly
// causes test/build failures due to the drawings-frontend barrel pulling in
// Google Maps dependencies at module evaluation time. This isn't long for this
// world anyway (going to have to rewrite drawings frontend due to Google maps
// deprecating API's) so going to avoid the complexity of reworking it.
const MAX_RADIUS_METERS = 500_000 / 2;

interface DrawingItemPoint {
  geometry: GeoJSON.Point;
}

export type RadiusInputProps = {
  updateGeometry: (scale: number, drawing: AllGeoJSON) => void;
  onChange?: (value: number) => void;
  drawing: DrawingItem | DrawingItemPoint | null | undefined;
  isSmallScale: boolean;
  unitPreference: MeasurementSystem;
  getRadiusMeters: (drawing: DrawingItem | DrawingItemPoint | null | undefined) => number;
};

export const RadiusInput: FC<RadiusInputProps> = ({
  updateGeometry,
  onChange,
  drawing,
  isSmallScale,
  unitPreference,
  getRadiusMeters,
}) => {
  const metersToConfiguredUnit = (valueMeters: number) =>
    Math.round(
      (radiusInUserUnits({ radius: valueMeters, unitPreference, isSmallScale }) + Number.EPSILON) *
        (isSmallScale ? 1 : 100)
    ) / (isSmallScale ? 1 : 100);

  const { t } = useTranslation();
  const maxRadiusConfiguredUnits = metersToConfiguredUnit(MAX_RADIUS_METERS);
  const maxRadiusMinutes = Number(metersToWalkingMinutes(MAX_RADIUS_METERS).toFixed(1));

  const onRadiusInputHandler = debounce((value, drawing) => {
    if (!Number.isFinite(value)) return;

    const newValue = Math.min(value, maxRadiusConfiguredUnits);

    if (newValue && drawing) {
      updateGeometry(
        // use a rounded value to avoid floating point errors
        newValue /
          (Math.round(radiusInUserUnits({ radius: getRadiusMeters(drawing), unitPreference, isSmallScale }) * 100) /
            100),
        drawing
      );
    }

    onChange?.(convertUserUnitsToMeters(newValue, { unitPreference, smallUnitSize: isSmallScale }));
  }, 100);

  const onWalkingInputHandler = debounce((value, drawing) => {
    if (!Number.isFinite(value)) return;

    const newValue = Math.min(value, maxRadiusMinutes);
    const distance = walkingMinutesToMeters(newValue);

    if (newValue && drawing) {
      updateGeometry(distance / getRadiusMeters(drawing), drawing);
    }
    onChange?.(distance);
  }, 100);

  const handleOnKeyDown = (event: DrawingsKeyboardEvent) => {
    if (event.key === "Enter" && drawing && event.currentTarget.id === "walking-input") {
      onWalkingInputHandler(Number(event.currentTarget.value), drawing);
    }
    if (event.key === "Enter" && drawing && event.currentTarget.id === "radius-input") {
      onRadiusInputHandler(Number(event.currentTarget.value), drawing);
    }
  };

  return (
    <div className={"flex flex-row items-center"} data-testid="radius-input-wrapper">
      <NumericInput
        className={"bg-white  text-text-base-primary"}
        id="radius-input"
        data-testid="radius-input"
        isDisabled={!drawing}
        step={isSmallScale ? 1 : 0.1}
        onChange={(v) => onRadiusInputHandler(v, drawing)}
        minValue={isSmallScale ? 1 : 0.1}
        maxValue={maxRadiusConfiguredUnits}
        value={metersToConfiguredUnit(getRadiusMeters(drawing))}
        units={unitOfMeasurement({ unitPreference, small: isSmallScale })}
        onKeyDown={handleOnKeyDown}
      />
      <span className={"mx-2 "} />
      <NumericInput
        className={"bg-white  text-text-base-primary"}
        id="walking-input"
        data-testid="walking-input"
        isDisabled={!drawing}
        step={0.1}
        minValue={0.1}
        maxValue={Number(metersToWalkingMinutes(MAX_RADIUS_METERS).toFixed(1))}
        onChange={(v) => onWalkingInputHandler(v, drawing)}
        value={Number(metersToWalkingMinutes(getRadiusMeters(drawing)).toFixed(1))}
        units={t("comparables.calculator.drawingTools.walking.unit")}
        onKeyDown={handleOnKeyDown}
      />
    </div>
  );
};
